import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    BookOpen,
    FileText,
    Home,
    Inbox,
    KeyRound,
    LogOut,
    MapPinned,
    Menu as MenuIcon,
    PlusCircle,
    PlugZap,
    Settings as SettingsIcon,
    Warehouse,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import api from "@/lib/api";
import { useAuth, isAdmin, ROLE_LABELS } from "@/lib/auth.jsx";
import { getApiErrorMessage } from "@/lib/apiError";

const ADMIN_NAV = [
    { to: "/dashboard", label: "Quote Dashboard", icon: Home, testid: "sidebar-nav-dashboard-link" },
    { to: "/ready-to-quote", label: "Ready to Quote", icon: Inbox, testid: "sidebar-nav-ready-to-quote-link" },
    { to: "/quotes", label: "Quote Library", icon: FileText, testid: "sidebar-nav-quotes-link" },
    { to: "/home-catalog", label: "Home Catalog", icon: BookOpen, testid: "sidebar-nav-home-catalog-link" },
    { to: "/home-inventory", label: "Home Inventory", icon: Warehouse, testid: "sidebar-nav-home-inventory-link" },
    { to: "/property-packages", label: "Property Packages", icon: MapPinned, testid: "sidebar-nav-property-packages-link" },
];

const QUOTE_PORTAL_NAV = [
    { to: "/dashboard", label: "Quote Dashboard", icon: Home, testid: "sidebar-nav-dashboard-link" },
    { to: "/ready-to-quote", label: "Ready to Quote", icon: Inbox, testid: "sidebar-nav-ready-to-quote-link" },
    { to: "/quotes", label: "Quote Library", icon: FileText, testid: "sidebar-nav-quotes-link" },
    { to: "/home-catalog", label: "Home Catalog", icon: BookOpen, testid: "sidebar-nav-home-catalog-link" },
    { to: "/home-inventory", label: "Home Inventory", icon: Warehouse, testid: "sidebar-nav-home-inventory-link" },
    { to: "/property-packages", label: "Property Packages", icon: MapPinned, testid: "sidebar-nav-property-packages-link" },
];

function LogoTile({ size = "large" }) {
    const dimensions = size === "small" ? "h-10 w-10 p-1.5" : "h-12 w-12 p-2";
    return (
        <div className={`flex ${dimensions} shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm`}>
            <img src="/ehs-logo.webp" alt="Easy HomeSource" className="h-full w-full object-contain" />
        </div>
    );
}

function Brand() {
    return (
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-5">
            <LogoTile />
            <div className="min-w-0">
                <div className="font-semibold leading-tight text-slate-900">Easy HomeSource</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Quote Portal</div>
            </div>
        </div>
    );
}

function NavItem({ item, onNavigate }) {
    return (
        <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => onNavigate(item.to, true)}
            className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
                    isActive
                        ? "border border-[rgba(67,161,215,0.22)] bg-[rgba(67,161,215,0.12)] text-[#0F2A47]"
                        : "text-slate-700 hover:bg-slate-50"
                }`
            }
        >
            <item.icon className="h-4 w-4" />
            {item.label}
        </NavLink>
    );
}

function NavBody({ user, onNavigate, onLogout, onAccountSecurity }) {
    const adminUser = isAdmin(user);
    const navItems = adminUser ? ADMIN_NAV : QUOTE_PORTAL_NAV;

    return (
        <>
            <div className="px-3 py-3">
                <Button
                    data-testid="sidebar-new-quote-button"
                    className="w-full bg-[#0F2A47] text-white hover:bg-[#14385E]"
                    onClick={() => onNavigate("/quotes/new")}
                >
                    <PlusCircle className="mr-2 h-4 w-4" /> New Manual Quote
                </Button>
            </div>

            <nav className="flex-1 space-y-1 px-2 py-2">
                {navItems.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} />)}
            </nav>

            {adminUser && (
                <div className="space-y-1 px-2 pb-2">
                    <NavLink
                        to="/integrations"
                        data-testid="sidebar-nav-integrations-link"
                        onClick={() => onNavigate("/integrations", true)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
                                isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                            }`
                        }
                    >
                        <PlugZap className="h-4 w-4" /> GHL Import Settings
                    </NavLink>
                    <NavLink
                        to="/settings"
                        data-testid="sidebar-nav-settings-link"
                        onClick={() => onNavigate("/settings", true)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
                                isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                            }`
                        }
                    >
                        <SettingsIcon className="h-4 w-4" /> Pricing / Users
                    </NavLink>
                </div>
            )}

            <div className="space-y-2 border-t border-slate-100 px-3 py-3">
                <div className="flex items-center gap-3 px-1">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--secondary))] text-sm font-semibold text-[#0F2A47]">
                        {(user?.name || user?.email || "U")
                            .split(" ")
                            .map((s) => s[0])
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
                        <div className="text-xs text-slate-500">{ROLE_LABELS[user?.role] || user?.role || "User"}</div>
                    </div>
                </div>
                <Button
                    data-testid="sidebar-account-security-button"
                    variant="outline"
                    onClick={onAccountSecurity}
                    className="w-full justify-start text-slate-700 hover:border-[#43a1d7] hover:text-[#0F2A47]"
                >
                    <KeyRound className="mr-2 h-4 w-4" /> Change Password
                </Button>
                <Button
                    data-testid="sidebar-logout-button"
                    variant="outline"
                    onClick={onLogout}
                    className="w-full justify-start text-slate-700 hover:border-[#43a1d7] hover:text-[#0F2A47]"
                >
                    <LogOut className="mr-2 h-4 w-4" /> Sign Out
                </Button>
            </div>
        </>
    );
}

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
        setMobileOpen(false);
    };

    const handleAccountSecurity = async () => {
        const currentValue = window.prompt("Enter your current password");
        if (currentValue === null) return;
        if (!currentValue) {
            toast.error("Current password is required");
            return;
        }

        const newValue = window.prompt("Enter your new password. It must be at least 8 characters.");
        if (newValue === null) return;
        if (newValue.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }

        const confirmValue = window.prompt("Confirm your new password");
        if (confirmValue === null) return;
        if (newValue !== confirmValue) {
            toast.error("New passwords do not match");
            return;
        }

        try {
            await api.post("/auth/change-password", {
                current_password: currentValue,
                new_password: newValue,
            });
            toast.success("Password changed. Please sign back in with your new password.");
            handleLogout();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Unable to change password"));
        }
    };

    const onNavigate = (to, fromNav = false) => {
        if (fromNav) {
            setMobileOpen(false);
        } else {
            navigate(to);
            setMobileOpen(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]">
            <div className="flex min-h-screen">
                <aside className="hidden w-[260px] shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
                    <Brand />
                    <NavBody user={user} onNavigate={onNavigate} onLogout={handleLogout} onAccountSecurity={handleAccountSecurity} />
                </aside>

                <main className="min-w-0 flex-1">
                    <div className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
                        <div className="flex items-center gap-2">
                            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" data-testid="mobile-menu-button" aria-label="Open navigation">
                                        <MenuIcon className="h-5 w-5" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="flex w-[280px] flex-col p-0">
                                    <Brand />
                                    <NavBody user={user} onNavigate={onNavigate} onLogout={handleLogout} onAccountSecurity={handleAccountSecurity} />
                                </SheetContent>
                            </Sheet>
                            <div className="flex items-center gap-2">
                                <LogoTile size="small" />
                                <div className="font-semibold text-slate-900">EHS Quotes</div>
                            </div>
                        </div>
                        <Button
                            data-testid="mobile-new-quote-button"
                            size="sm"
                            className="bg-[#0F2A47] text-white hover:bg-[#14385E]"
                            onClick={() => navigate("/quotes/new")}
                        >
                            <PlusCircle className="mr-1 h-4 w-4" /> New
                        </Button>
                    </div>
                    <div className="px-4 py-6 sm:px-6 lg:px-8">{children}</div>
                </main>
            </div>
        </div>
    );
}
